import React, { useState } from 'react';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import contactus from '../assets/contactus.webp';
import useResponsive from '../hooks/useResponsive';
import { ContactUs } from '../services/apiService';

const Contactus = () => {
    const { isSmallScreen } = useResponsive();

    // Form state with correct keys
    const [form, setForm] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        address: '',
        enquiry: '',
        message: '',
    });

    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [errors, setErrors] = useState({});

    // Handle input changes & clear errors on change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Simple client-side phone validation
        const phoneDigitsOnly = form.phoneNumber.replace(/\D/g, '');
        if (phoneDigitsOnly.length !== 10) {
            setErrors({ phoneNumber: 'Phone number must be exactly 10 digits.' });
            setErrorMsg('Please fix the errors below.');
            return;
        }

        // Proceed with API call
        setLoading(true);
        setSuccessMsg('');
        setErrorMsg('');
        setErrors({});

        try {
            const response = await ContactUs(form);
            if (response.status) {
                setForm({
                    fullName: '',
                    email: '',
                    phoneNumber: '',
                    address: '',
                    enquiry: '',
                    message: '',
                });
                setSuccessMsg('Message sent successfully!');
                setErrorMsg('');
            } else {
                setSuccessMsg('');
                setErrorMsg(response.message || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            setSuccessMsg(''); // Clear success message on error
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                setErrorMsg('Please fix the errors below.');
            } else {
                setErrorMsg('Failed to send message. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container  sx={{ py: 3, mt: 14 }}>
            {/* Section Heading */}
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 3 }}> Contact Us </Typography>

            {/* Tagline Banner */}
            <Box
                sx={{
                    mt: isSmallScreen ? 5 : 6,
                    px: isSmallScreen ? 1 : 3,
                    py: isSmallScreen ? 1.5 : 3,
                    borderRadius: 2,
                    backgroundColor: '#ffb627',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    textAlign: isSmallScreen ? 'center' : 'left',
                    position: 'relative',
                    overflow: 'visible',
                    minHeight: isSmallScreen ? 140 : 145,
                }}
            >
                <Box sx={{ flex: 1, zIndex: 1 }}>
                    <Typography variant="h6" sx={{
                        fontStyle: 'italic', fontWeight: 500,
                        fontSize: isSmallScreen ? 16 : 20,
                        mt: isSmallScreen ? 4 : 0,
                    }}
                    >
                        Choose Vibrant Propmart
                    </Typography>
                    <Typography variant="h6" sx={{
                        fontStyle: 'italic', fontWeight: 500,
                        fontSize: isSmallScreen ? 16 : 20,
                    }}
                    >
                        Because Your Home Deserves More Than Just a Transaction.
                    </Typography>
                </Box>

                <Box component="img" loading='lazy' src={contactus} alt="Customer Support"
                    sx={{
                        position: 'absolute', right: isSmallScreen ? '50%' : 24,
                        top: isSmallScreen ? -50 : -40,
                        maxHeight: isSmallScreen ? 100 : 260,
                        objectFit: 'contain',
                        maxWidth: isSmallScreen ? 120 : 250,
                        zIndex: 2,
                        transform: isSmallScreen ? 'translateX(50%)' : 'none',
                    }}
                />
            </Box>

            {/* Contact & Office Section */}
            <Box
                sx={{
                    mt: 4,
                    display: { xs: 'block', md: 'flex' },
                    gap: 4,
                    alignItems: 'flex-start',
                }}
            >
                {/* Office Address */}
                <Box sx={{ flex: '1 1 260px', mb: { xs: 3, md: 0 } }}>
                    <Typography variant="h5" fontWeight={600} fontSize="20px" mb={2}>
                        Registered Office:
                    </Typography>

                    {/* Address */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2.5 }}>
                        <Box sx={{
                            bgcolor: '#BB081B17',
                            borderRadius: '50%',
                            p: 2.7,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        >
                            <i
                                className="fas fa-map-marker-alt"
                                style={{ color: '#BB081B', fontSize: '20px' }}
                            />
                        </Box>
                        <Typography variant="h5" color="#222">
                            Plot No:411,Flat No:201, SreeNivas Apartment,Gate No:1,Near Miyapur
                            Metro station,Mathrusrisri Nagar, Miyapur, Hyderabad, Telangana-
                            500049.
                        </Typography>
                    </Box>

                    {/* Email */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2.5 }}>
                        <Box sx={{
                            bgcolor: '#BB081B17', borderRadius: '50%',
                            p: 2.7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                            <i className="fas fa-envelope" style={{ color: '#BB081B', fontSize: '20px' }}
                            />
                        </Box>
                        <Typography variant="h5" color="#222">
                            vibrantpropmarthyd@gmail.com
                        </Typography>
                    </Box>

                    {/* Phone */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box
                            sx={{
                                bgcolor: '#BB081B17',
                                borderRadius: '50%',
                                p: 2.7,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <i
                                className="fas fa-phone"
                                style={{ color: '#BB081B', fontSize: '20px' }}
                            />
                        </Box>
                        <Typography variant="h5" color="#222">
                            +91 9705401575
                        </Typography>
                    </Box>
                </Box>

                {/* Contact Form */}
                <Box sx={{ flex: '2 1 400px', minWidth: 320 }}>
                    <Typography variant="h5" fontWeight={600} mb={2} fontSize="20px">
                        Message Us:
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                        {/* Name & Email */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                mb: 1.5,
                                flexDirection: { xs: 'column', sm: 'row' },
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder="Name"
                                fullWidth
                                name="fullName"
                                value={form.fullName}
                                onChange={handleChange}
                                required
                                error={!!errors.fullName}
                                helperText={errors.fullName}
                                sx={{ flex: 1, bgcolor: '#f7f7f7', borderRadius: 1 }}
                                InputProps={{ style: { fontSize: 14 } }}
                            />
                            <TextField
                                size="small"
                                placeholder="Email"
                                fullWidth
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                required
                                error={!!errors.email}
                                helperText={errors.email}
                                sx={{ flex: 1, bgcolor: '#f7f7f7', borderRadius: 1 }}
                                InputProps={{ style: { fontSize: 14 } }}
                            />
                        </Box>

                        {/* Phone & Enquire About in a row */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                mb: 1.5,
                                flexDirection: { xs: 'column', sm: 'row' },
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder="Phone"
                                fullWidth
                                name="phoneNumber"
                                type="tel"
                                value={form.phoneNumber}
                                onChange={(e) => {
                                    // Only allow numbers and max 10 digits
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    setForm((prev) => ({ ...prev, phoneNumber: val }));
                                    setErrors((prev) => ({ ...prev, phoneNumber: '' }));
                                }}
                                required
                                error={!!errors.phoneNumber}
                                helperText={errors.phoneNumber}
                                inputProps={{ maxLength: 10, pattern: "[0-9]{10}" }}
                                sx={{ flex: 1, bgcolor: '#f7f7f7', borderRadius: 1 }}
                                InputProps={{ style: { fontSize: 14 } }}
                            />
                            <FormControl
                                size="small"
                                sx={{ flex: 1, bgcolor: '#f7f7f7', borderRadius: 1 }}
                            >
                                <Select
                                    displayEmpty
                                    name="enquiry"
                                    value={form.enquiry}
                                    onChange={handleChange}
                                    inputProps={{ 'aria-label': 'Enquire About' }}
                                    sx={{ fontSize: 14 }}
                                >
                                    <MenuItem value="" disabled>
                                        Enquire About
                                    </MenuItem>
                                    <MenuItem value="Price">Price</MenuItem>
                                    <MenuItem value="Availability">Availability</MenuItem>
                                    <MenuItem value="Amenities">Amenities</MenuItem>
                                    <MenuItem value="Site Visit">Site Visit</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Message */}
                        <Box sx={{ mb: 1.5 }}>
                            <TextField
                                placeholder="Message"
                                multiline
                                minRows={4}
                                fullWidth
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                sx={{ bgcolor: '#f7f7f7', borderRadius: 1 }}
                                InputProps={{ style: { fontSize: 14 } }}
                            />
                        </Box>

                        {/* Success/Error Messages */}
                        {successMsg && (
                            <Typography color="green" sx={{ mb: 1 }}>
                                {successMsg}
                            </Typography>
                        )}
                        {errorMsg && (
                            <Typography color="red" sx={{ mb: 1 }}>
                                {errorMsg}
                            </Typography>
                        )}

                        {/* Submit Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={loading}
                                sx={{
                                    bgcolor: '#BB081B',
                                    color: '#fff',
                                    borderRadius: 2,
                                    px: 3,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    textTransform: 'none',
                                }}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Google Map */}
            <Box sx={{ mt: 5 }}>
                <Typography variant="h5" fontWeight={600} mb={1} fontSize="20px">
                    Our Location:
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        minHeight: 150,
                        height: 210,
                        bgcolor: '#f7f7f7',
                        borderRadius: 2,
                        border: '1px solid #eee',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d552713.5889529091!2d78.05141519860342!3d17.330263560527943!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb8fe3d7b207b7%3A0x4e23519a3c5ca27a!2sVibrant%20Propmart!5e0!3m2!1sen!2sin!4v1751978365881!5m2!1sen!2sin"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Google Map"
                    ></iframe>
                </Box>
            </Box>
        </Container>
    );
};

export default Contactus;
